import { useEffect, useRef, useState, type ReactNode } from 'react'
import { AnimatePresence, m } from 'motion/react'
import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { Check, ChevronDown, Keyboard, Languages, LogOut, Moon, Settings, Sun } from 'lucide-react'
import type { Me } from '@nw/shared/api/auth'
import { ApiError, api } from '../../lib/api.ts'
import { useLocale } from '../../lib/i18n.tsx'
import { flushOutbox, outboxCount, outboxDiscard } from '../../lib/outbox.ts'
import { useTheme } from '../../lib/theme.ts'
import { Avatar } from '../../kit/Avatar.tsx'
import { Kbd } from '../../kit/Kbd.tsx'
import { useToast } from '../../kit/Toast.tsx'
import { cn, duration } from '../../kit/util.ts'
import { openOverlay } from '../ui.ts'

function Row({ children, onClick, pressed, kbd }: { children: ReactNode; onClick: () => void; pressed?: boolean; kbd?: string[] }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={pressed}
      className="flex h-9 w-full items-center gap-2.5 rounded-md px-2 text-left text-sm text-ink-muted transition hover:bg-hover hover:text-ink"
    >
      {children}
      {pressed && <Check size={14} strokeWidth={2.25} aria-hidden className="ml-auto text-accent-text" />}
      {kbd && <Kbd keys={kbd} tone="plain" className="ml-auto" />}
    </button>
  )
}

const segment = (on: boolean) =>
  cn(
    'flex h-8 items-center justify-center gap-2 rounded-md text-sm transition',
    on ? 'bg-raised text-ink shadow-raised ring-2 ring-indicator ring-inset' : 'text-ink-muted hover:bg-hover',
  )

/** Theme and language as two segmented rows (the account menu and the phone's More sheet). */
export function Preferences() {
  const { t, locale, setLocale } = useLocale()
  const [theme, setTheme] = useTheme()
  return (
    <>
      <div className="grid grid-cols-2 gap-1 p-0.5" role="group" aria-label={t('common.theme.dark') + ' / ' + t('common.theme.light')}>
        {(['dark', 'light'] as const).map((th) => (
          <button key={th} type="button" aria-pressed={theme === th} onClick={() => setTheme(th)} className={segment(theme === th)}>
            {th === 'dark' ? <Moon size={14} aria-hidden /> : <Sun size={14} aria-hidden />}
            {t(`common.theme.${th}`)}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-1 p-0.5" role="group" aria-label={t('common.language.label')}>
        {(['nb', 'en'] as const).map((l) => (
          <button key={l} type="button" lang={l} aria-pressed={locale === l} onClick={() => setLocale(l)} className={segment(locale === l)}>
            <Languages size={14} aria-hidden />
            {t(`common.language.${l}`)}
          </button>
        ))}
      </div>
    </>
  )
}

/**
 * Signs out through the API; the cookie is HttpOnly, so only the server can end the session. Touches queued on this device
 * (lib/outbox.ts) would go with the session, so while any wait, sign-out is refused unless the person discards them
 * (CONTRACTS.md 22.9): the refusal is a toast that waits, with Discard and sign out. The queue is dropped after the
 * session has ended, so a sign-out that fails (offline) loses nothing.
 */
export function useSignOut(): () => Promise<void> {
  const { t } = useLocale()
  const toast = useToast()
  const queryClient = useQueryClient()
  const router = useRouter()
  const end = async (): Promise<boolean> => {
    try {
      await api.post('/api/auth/logout')
    } catch (e) {
      const code = e instanceof ApiError ? e.code : 'internal'
      if (code !== 'unauthenticated') {
        toast.show({ text: code === 'offline' ? t('common.needsConnection') : t(`error.${code === 'bad_response' ? 'internal' : code}`), tone: 'danger' })
        return false
      }
    }
    queryClient.clear()
    await router.navigate({ to: '/login', replace: true })
    return true
  }
  return async () => {
    const waiting = await outboxCount()
    if (waiting === 0) return void (await end())
    const discard = () => void end().then(async (out) => void (out && (await outboxDiscard())))
    toast.show({ text: t('settings.signOut.refused', { count: waiting }), tone: 'danger', action: { label: t('settings.signOut.discard'), run: discard } })
  }
}

/** Sends the touches queued on this device whenever the app is open and online: on load, on `online`, every 15 seconds. */
function useOutboxFlush(userId: number): void {
  const { t } = useLocale()
  const toast = useToast()
  const queryClient = useQueryClient()
  const sent = useRef((_n: number) => {})
  useEffect(() => {
    sent.current = (n) => {
      if (n === 0) return
      toast.show({ text: t('touch.queue.sent', { count: n }), tone: 'ok' })
      void queryClient.invalidateQueries({ queryKey: ['touch'] })
      void queryClient.invalidateQueries({ queryKey: ['org'] })
    }
  }, [t, toast, queryClient])
  useEffect(() => {
    const run = () => void flushOutbox(userId).then((n) => sent.current(n))
    run()
    const timer = window.setInterval(run, 15_000)
    window.addEventListener('online', run)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('online', run)
    }
  }, [userId])
}

/** Avatar button with theme, language, Settings, shortcuts and sign out. */
export function UserMenu({ me }: { me: Me }) {
  const { t } = useLocale()
  const signOut = useSignOut()
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  useOutboxFlush(me.id)

  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => !box.current?.contains(e.target as Node) && setOpen(false)
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setOpen(false)
      trigger.current?.focus()
    }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Focus goes back to the avatar button, so a keyboard user is not dropped on <body>; also after a sign-out that is refused.
  const pick = (fn: () => void) => () => {
    setOpen(false)
    trigger.current?.focus()
    fn()
  }

  return (
    <div ref={box} className="relative">
      <button
        ref={trigger}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={t('common.account.label', { name: me.displayName })}
        className="flex h-9 items-center gap-1.5 rounded-md pr-1.5 pl-1 transition hover:bg-hover"
      >
        <Avatar name={me.displayName} size="sm" />
        <ChevronDown size={14} strokeWidth={2} aria-hidden className="hidden text-ink-faint sm:block" />
      </button>
      <AnimatePresence>
        {open && (
          <m.div
            role="dialog"
            aria-label={t('common.account.menu')}
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: duration('fast') }}
            className="absolute top-full right-0 z-50 mt-2 w-72 origin-top-right rounded-lg bg-overlay p-1.5 shadow-overlay"
          >
            <div className="flex items-center gap-3 px-2 py-2">
              <Avatar name={me.displayName} size="md" />
              <div className="min-w-0">
                <div className="truncate text-base font-semibold text-ink">{me.displayName}</div>
                <div className="text-xs text-ink-faint">{me.role === 'owner' ? t('common.account.owner') : t('common.account.member')}</div>
              </div>
            </div>
            <div className="my-1.5 h-px bg-line" />
            <Preferences />
            <div className="my-1.5 h-px bg-line" />
            <Row onClick={pick(() => void router.navigate({ to: '/settings' }))} kbd={[',']}>
              <Settings size={15} strokeWidth={1.75} aria-hidden /> {t('nav.settings')}
            </Row>
            <Row onClick={pick(() => openOverlay('shortcuts'))} kbd={['?']}>
              <Keyboard size={15} strokeWidth={1.75} aria-hidden /> {t('common.shortcuts.title')}
            </Row>
            <Row onClick={pick(() => void signOut())}>
              <LogOut size={15} strokeWidth={1.75} aria-hidden /> {t('common.account.signOut')}
            </Row>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  )
}
